/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  DeliveryType,
  // getAddressLabels,
  PaymentMethodCode,
  type IntercityPath,
  type OrderRequest,
  type Path,
  type Service,
} from '@ahm/api-wrappers-core';
import { cleanObject } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { useLocationStore } from '@ahm/react-map/store';
import { toast } from '@ahm/react-ui';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import { ROUTES } from '@/config/router';
import { useGetIntercityOrderDetail } from '@/features/booking/api/intercity';
import { useGetDraftDetail } from '@/features/booking/api/order/use-get-draft-detail';
import { useGetOrderDetail } from '@/features/booking/api/order/use-get-order-detail';
import { usePaymentMethodHook } from '@/hooks/use-payment-method';
import { SERVICE_HAVE_HUB_SP_TRANSFER_COD_REQUEST } from '@/lib/constants';
import { transformPath } from '@/lib/utils';
import { usePathname } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface FillDataProps {
  handleFillData: () => void;
}

interface DiscountDetail {
  promo_code_id: string;
  discount_value: number;
  description: string;
  category: string;
}

export const useFillData = (): FillDataProps => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const orderId = searchParams.get('order_id') ?? '';
  const paymentAction = searchParams.get('paymentAction');
  const draftId = searchParams.get('draft_id') ?? '';
  const tPayment = useTranslations('payment');
  const router = useRouter();

  const {
    serviceTypes,
    setPathInfoByIndex,
    estimatedRequestData,
    setIsLoadingStore,
    setStoreData,
    setPaymentMethod,
    // setTruckSelectedGroup,
  } = useBookingStore((state) => state);

  const { setPaths } = useLocationStore((state) => state);
  const { validPaymentMethodList } = usePaymentMethodHook();
  const { session } = useAuth();
  const user = session?.user;
  const partner = user?.partner;

  const isCommonValid = useMemo(
    () => Boolean(user) && validPaymentMethodList?.length > 0,
    [user, validPaymentMethodList]
  );

  const isOrderDetaiIntercityEnabled = useMemo(
    () => pathname === ROUTES.nationwide && Boolean(orderId) && isCommonValid,
    [pathname, orderId, isCommonValid]
  );

  const isOrderDetailEnabled = useMemo(() => {
    const validRoutes = [ROUTES.truck, ROUTES.home, ROUTES.newOrder];
    return validRoutes.includes(pathname) && Boolean(orderId) && isCommonValid;
  }, [pathname, orderId, isCommonValid]);

  const { data: dataIntercityOrderDetail } = useGetIntercityOrderDetail(
    orderId,
    {
      enabled: isOrderDetaiIntercityEnabled,
    }
  );

  const { data: dataOrderDetail } = useGetOrderDetail(orderId, {
    enabled: isOrderDetailEnabled,
  });

  const { data: draftData } = useGetDraftDetail(draftId, {
    enabled: Boolean(draftId) && isCommonValid,
  });

  const orderDetail = isOrderDetaiIntercityEnabled
    ? dataIntercityOrderDetail
    : dataOrderDetail;

  const updateUrlWithParams = useCallback(
    (params = {}) => {
      const newSearchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            newSearchParams.set(key, value.join(','));
          } else {
            newSearchParams.set(key, String(value));
          }
        }
      });
      router.replace(`${pathname}?${newSearchParams.toString()}`);
    },
    [pathname, router]
  );

  const startCountdown = useCallback((seconds = 2) => {
    const timer = setInterval(() => {
      if (seconds === 0) {
        setIsLoadingStore(false);
        clearInterval(timer);
      } else {
        seconds--;
      }
    }, 1000);
  }, []);

  const transformNationwidePath = useCallback(
    (path: IntercityPath[], service: any) => {
      if (service?.delivery_type === DeliveryType.Nationwide) {
        const {
          item_value,
          item_description,
          item_name,
          item_order_id,
          ...stopPoint
        } = path[1];

        setPathInfoByIndex(
          '1',
          cleanObject({
            item_value,
            item_description,
            item_name,
            item_order_id,
          })
        );

        return [path[0], stopPoint];
      }
      return path;
    },
    [setPathInfoByIndex]
  );

  const setupPath = useCallback(
    (paths: Path[] | IntercityPath[], service: Service) => {
      if (!paths?.length || paths.length <= 1) return;

      const transformedPaths = paths.map((path) => transformPath(path));

      const finalPaths = transformNationwidePath(
        transformedPaths as IntercityPath[],
        service
      );
      setPaths(finalPaths as Path[]);
    },
    [transformNationwidePath, setPaths]
  );

  const transformPaymentMethod = useCallback(
    (orderDetail) => {
      if (!orderDetail || !validPaymentMethodList?.length) {
        return null;
      }

      if (partner) {
        const defaultMethod = validPaymentMethodList.find(
          (item) => item.is_default
        );
        if (defaultMethod) {
          return defaultMethod;
        }
      }

      const methodId = orderDetail.payment_details?.method_id;
      if (methodId) {
        const matchedMethod = validPaymentMethodList.find(
          (item) => item.method_id === methodId
        );
        if (matchedMethod) {
          return matchedMethod;
        }
      }

      return validPaymentMethodList[0] ?? null;
    },
    [validPaymentMethodList, partner]
  );

  const transformBillableWeight = useCallback(
    (packageDetail: any, packageDimensions: any) => {
      if (!packageDetail || !packageDimensions) return null;

      return {
        dim_factor: packageDimensions.dim_factor,
        total_billable_weight: packageDimensions.billable_weight,
        total_volumetric_weight: packageDimensions.volumetric_weight,
        package_weight_details: packageDetail.map((item: any) => ({
          billable_weight: item.billable_weight,
          volumetric_weight: item.volumetric_weight,
        })),
      };
    },
    []
  );

  const transformServiceRequests = useCallback((requests: OrderRequest[]) => {
    if (!requests || requests.length === 0) return {};
    const serviceRequests = {};
    const D2DRequests = requests.filter((req) => req?._id.includes('D2D'));
    if (D2DRequests?.length) {
      serviceRequests[D2DRequests[0]?._id] = D2DRequests.map(
        (item) => item?.path_index
      );
    }
    const loadingGoodsRequests = requests.filter((req) =>
      req?._id.includes('LOADING-OPTIONS')
    );
    if (loadingGoodsRequests?.length) {
      serviceRequests[loadingGoodsRequests[0]?._id] = loadingGoodsRequests.map(
        (item) => {
          const result = { ...item };
          if (result?.options) {
            result.options = result.options.map((option: any) => {
              const newOption = { ...option };
              delete newOption.price;
              return newOption;
            });
          }
          delete result.price;
          return result;
        }
      );
    }
    requests.forEach((item: OrderRequest) => {
      if (
        item?._id.includes('D2D') ||
        item?._id.includes('BULKY') ||
        item?._id.includes('LOADING-OPTIONS')
      )
        return;

      if (item?._id.includes('TRANSFER-COD')) {
        const serviceIdWithoutSuffix = item?._id.replace('-TRANSFER-COD', '');
        if (
          SERVICE_HAVE_HUB_SP_TRANSFER_COD_REQUEST.includes(
            serviceIdWithoutSuffix
          )
        ) {
          serviceRequests[item?._id] = true;
        }
        return;
      }

      if (item?._id.includes('FRAGILE')) {
        serviceRequests[item?._id] = {
          option_code: item.option_code,
          option_detail: item.option_detail,
        };
      } else {
        serviceRequests[item?._id] = item?.num || true;
      }
    });

    return serviceRequests;
  }, []);

  const processDetailFlow = useCallback(
    (orderDetail) => {
      setIsLoadingStore(true);
      startCountdown();
      const {
        path,
        package_images,
        service_id,
        service,
        images,
        remarks,
        requests,
        package_detail,
        package_dimensions,
        package_size,
        package_weight,
        item_descriptions,
        // promo_code,
        discount_details,
      } = orderDetail;

      if (!path?.length) return;

      setupPath(path, service);
      const selectedService =
        serviceTypes?.find((s) => s._id === service_id) ?? null;
      const billableWeightData = transformBillableWeight(
        package_detail,
        package_dimensions
      );
      const serviceRequests = transformServiceRequests(requests);
      const paymentMethod = transformPaymentMethod(orderDetail);
      const promo_code_ids =
        (discount_details as DiscountDetail[])?.map(
          (item) => item.promo_code_id
        ) || [];
      setStoreData({
        selectedService,
        billableWeightData,
        packageDetail: package_detail,
        packageImages: package_images,
        images,
        remarks,
        serviceRequests,
        bikePackageSize: package_size,
        bikePackageWeight: package_weight,
        itemDescriptions: item_descriptions,
        paymentMethod,
        promo_code_ids,

        // ...(selectedService.group_name_en_us?truckSelectedGroup)
        ...(selectedService?.group_name_en_us &&
          selectedService?.delivery_type === DeliveryType.Truck && {
            truckSelectedGroup: selectedService.group_name_en_us,
          }),
      });
      const urlQuery = {
        ...(!!promo_code_ids?.length && {
          promo_code: promo_code_ids,
        }),
      };
      updateUrlWithParams(urlQuery);
    },
    [serviceTypes, transformPaymentMethod]
  );

  const showNotification = useCallback(
    (isSuccess = true, message?: string) => {
      const type = paymentAction === 'link' ? 'link' : 'pay';
      const resultString = isSuccess ? 'success' : 'failed';
      const title = message ?? tPayment(`${type}_${resultString}`);

      toast[isSuccess ? 'success' : 'error']({
        id: 'payment-notification',
        title,
        withIcon: true,
        withCloseButton: false,
      });
    },
    [paymentAction]
  );

  const handleFillPaymentAction = useCallback(() => {
    if (!paymentAction || !validPaymentMethodList?.length) return null;

    const methodCode = (searchParams.get('methodCode') || '').toLowerCase();

    // === 1️⃣ Handle CARD LINKING (ATM / Visa / Credit card)
    const resultCd = searchParams.get('resultCd');
    if (resultCd === '00_000') {
      const cardNo = searchParams.get('cardNo');
      if (cardNo) {
        const cardMethod = validPaymentMethodList.find(
          (item) => item?.extra_data?.card_number === cardNo
        );
        showNotification(Boolean(cardMethod));
        return cardMethod ?? null;
      }
    }

    // === 2️⃣ Handle WALLET LINKING (Momo / Zalopay / Shopeepay) ===
    switch (methodCode) {
      case PaymentMethodCode.Momo.toLowerCase(): {
        const resultCode = searchParams.get('resultCode') || '';
        const message = decodeURIComponent(searchParams.get('message') || '');
        const isSuccess = resultCode === '0';

        showNotification(isSuccess, message);

        return (
          validPaymentMethodList.find(
            (item) =>
              item.code?.toLowerCase() === PaymentMethodCode.Momo.toLowerCase()
          ) ?? null
        );
      }

      case PaymentMethodCode.Zalopay.toLowerCase(): {
        const status = searchParams.get('status') || '';
        showNotification(status === '1');
        return (
          validPaymentMethodList.find(
            (item) =>
              item.code?.toLowerCase() ===
              PaymentMethodCode.Zalopay.toLowerCase()
          ) ?? null
        );
      }

      case PaymentMethodCode.Shopeepay.toLowerCase(): {
        const debugMsg = searchParams.get('debug_msg') || '';
        showNotification(debugMsg === 'success');
        return (
          validPaymentMethodList.find(
            (item) =>
              item.code?.toLowerCase() ===
              PaymentMethodCode.Shopeepay.toLowerCase()
          ) ?? null
        );
      }

      default:
        return null;
    }
  }, [paymentAction, searchParams, validPaymentMethodList, showNotification]);

  const processPaymentFlow = useCallback(
    (data) => {
      setIsLoadingStore(true);
      startCountdown();

      const {
        path,
        package_detail,
        promo_code_ids,
        package_images,
        package_size,
        package_weight,
        item_descriptions,
        services,
        order_time,
        serviceId,
        remarks,
        images,
      } = data;

      if (!serviceTypes || !path?.length || !services?.length) {
        setPaymentMethod(validPaymentMethodList[0]);

        const urlQuery = {
          ...(!!promo_code_ids?.length && {
            promo_code: promo_code_ids,
          }),
        };
        updateUrlWithParams(urlQuery);
        return;
      }

      setPaths(path as Path[]);
      const selectedService = serviceTypes?.find((s) => s._id === serviceId);
      const service = services.find((s) => s._id === serviceId);

      const requests = service?.requests || [];
      const serviceRequests = transformServiceRequests(requests);
      const paymentMethod = handleFillPaymentAction();
      if (!paymentMethod) {
        setPaymentMethod(validPaymentMethodList[0]);
        // const urlQuery = {
        //   ...(!!promo_code && { promo_code }),
        // };
        const urlQuery = {
          ...(!!promo_code_ids?.length && {
            promo_code: promo_code_ids,
          }),
        };
        updateUrlWithParams(urlQuery);
        return;
      }
      setStoreData({
        selectedService,
        packageDetail: package_detail,
        packageImages: package_images,
        images,
        remarks,
        // orderTime: order_time,
        serviceRequests,
        bikePackageSize: package_size,
        bikePackageWeight: package_weight,
        itemDescriptions: item_descriptions,
        paymentMethod: paymentMethod ?? validPaymentMethodList[0],
        promo_code_ids,
        // promoCode: promo_code,
      });
      const urlQuery = {
        ...(!!promo_code_ids?.length && {
          promo_code: promo_code_ids,
        }),
      };
      // const urlQuery = {
      //   ...(!!promo_code && { promo_code }),
      // };

      updateUrlWithParams(urlQuery);
    },
    [serviceTypes, handleFillPaymentAction]
  );

  const handleFillData = useCallback(() => {
    if (validPaymentMethodList.length === 0) return;
    if (orderId && orderDetail) {
      processDetailFlow(orderDetail);
    }
    if (draftId && draftData) {
      processDetailFlow(draftData);
    }
    if (paymentAction) {
      if (estimatedRequestData) processPaymentFlow(estimatedRequestData);
      else {
        setPaymentMethod(validPaymentMethodList[0]);
        const urlQuery = {};
        updateUrlWithParams(urlQuery);
      }
    }
  }, [
    orderDetail,
    processDetailFlow,
    estimatedRequestData,
    paymentAction,
    processPaymentFlow,
    validPaymentMethodList,
    draftData,
  ]);

  return {
    handleFillData,
  };
};
